import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Mail, MapPin, Send, Loader2, Instagram, Facebook, Lock, Clock } from "lucide-react";
import { toast } from "sonner";
import { fireConfetti, scrollToSection } from "../lib/site";
import { useContent } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_LINKS = [
  { label: "Experience", href: "#experience" },
  { label: "Packages", href: "#packages" },
  { label: "Add-Ons", href: "#addons" },
  { label: "Gallery", href: "/gallery" },
  { label: "About Us", href: "/about" },
  { label: "FAQ", href: "/faq" },
];

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useContent();
  const phone = settings?.phone || "+1 (345) 328-6172";
  const bizEmail = settings?.business_email || "info@glamboothcayman.com";
  const waLink = `https://wa.me/${phone.replace(/\D/g, "")}`;
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const go = (href) => {
    if (href.startsWith("/")) {
      navigate(href);
    } else if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: href } });
    } else {
      scrollToSection(href);
    }
  };

  const subscribe = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSubscribing(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      fireConfetti(e);
      toast.success("You're subscribed! Watch your inbox for glam updates.");
      setEmail("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-[#2a0028] relative grain pt-20 pb-28 md:pb-10" data-testid="site-footer">
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-[1.3fr_0.8fr_1fr_1.2fr] gap-12 lg:gap-10 mb-16">
          <div>
            <img
              src="/logo.png"
              alt="GlamBooth Cayman"
              className="h-24 w-auto object-contain brightness-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.4)] mb-5"
              data-testid="footer-logo"
            />
            <p className="text-white/55 text-sm leading-[1.8] max-w-xs">
              GlamBooth Cayman, your premier Photo Booth service provider
              dedicated to adding an extra layer of fun and excitement to your
              event.
            </p>
            <div className="flex gap-3 mt-6">
              {settings?.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="footer-instagram-link"
                  className="grid place-items-center w-10 h-10 rounded-full border border-white/15 text-white/60 hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors"
                >
                  <Instagram size={17} />
                </a>
              )}
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="footer-facebook-link"
                  className="grid place-items-center w-10 h-10 rounded-full border border-white/15 text-white/60 hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors"
                >
                  <Facebook size={17} />
                </a>
              )}
              {settings?.tiktok_url && (
                <a
                  href={settings.tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  data-testid="footer-tiktok-link"
                  className="grid place-items-center w-10 h-10 rounded-full border border-white/15 text-white/60 hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16" aria-hidden="true">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5.1 20.14a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
                  </svg>
                </a>
              )}
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp-icon-link"
                className="grid place-items-center w-10 h-10 rounded-full border border-white/15 text-white/60 hover:border-[#c9006f] hover:text-[#ffc4e4] transition-colors"
              >
                <MessageCircle size={17} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-serif text-white text-lg mb-6">Quick Links</h4>
            <ul className="space-y-3.5">
              {QUICK_LINKS.map((l) => (
                <li key={l.label}>
                  <button
                    onClick={() => go(l.href)}
                    data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-white/55 text-sm hover:text-[#ffc4e4] transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-white text-lg mb-6">Get In Touch</h4>
            <ul className="space-y-4 text-sm">
              <li>
                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="flex items-center gap-3 text-white/55 hover:text-[#ffc4e4] transition-colors"
                >
                  <MessageCircle size={16} className="text-[#c9006f] shrink-0" />
                  {phone}
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${bizEmail}`}
                  data-testid="footer-email-link"
                  className="flex items-center gap-3 text-white/55 hover:text-[#ffc4e4] transition-colors"
                >
                  <Mail size={16} className="text-[#c9006f] shrink-0" />
                  {bizEmail}
                </a>
              </li>
              <li className="flex items-center gap-3 text-white/55" data-testid="footer-address">
                <MapPin size={16} className="text-[#c9006f] shrink-0" />
                {settings?.address || "Grand Cayman & Sister Islands"}
              </li>
              <li className="flex items-center gap-3 text-white/55" data-testid="footer-hours">
                <Clock size={16} className="text-[#c9006f] shrink-0" />
                {settings?.hours || "Mon–Sun · 9:00 AM – 9:00 PM"}
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-serif text-white text-lg mb-3">Subscribe to Our Newsletter</h4>
            <p className="text-white/45 text-sm leading-relaxed mb-5">
              Seasonal offers, new backdrops, and booking reminders — straight to
              your inbox.
            </p>
            <form onSubmit={subscribe} className="flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="newsletter-email-input"
                className="flex-1 min-w-0 rounded-full bg-white/[0.07] border border-white/15 text-white placeholder:text-white/35 px-5 py-3 text-sm focus:border-[#c9006f] transition-colors"
              />
              <button
                type="submit"
                disabled={subscribing}
                data-testid="newsletter-subscribe-btn"
                aria-label="Subscribe"
                className="btn-gold rounded-full w-12 h-12 shrink-0 grid place-items-center disabled:opacity-60"
              >
                {subscribing ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Send size={18} />
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/35 text-sm text-center">
            © {new Date().getFullYear()} GlamBooth Cayman · Premium Photo Booth Rentals · Grand Cayman
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5">
            <button
              onClick={() => navigate("/terms")}
              data-testid="footer-terms-link"
              className="text-white/45 hover:text-[#ffc4e4] text-sm transition-colors"
            >
              Terms of Service
            </button>
            <button
              onClick={() => navigate("/privacy")}
              data-testid="footer-privacy-link"
              className="text-white/45 hover:text-[#ffc4e4] text-sm transition-colors"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => navigate("/admin")}
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1.5 text-white/45 hover:text-[#ffc4e4] text-sm transition-colors"
            >
              <Lock size={12} /> Admin
            </button>
            <button
              onClick={() => (window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.4 }) : window.scrollTo({ top: 0, behavior: "smooth" }))}
              className="text-white/45 hover:text-[#ffc4e4] text-sm transition-colors"
              data-testid="back-to-top-btn"
            >
              Back to top ↑
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export const StickyMobileCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const contact = document.querySelector("#contact");
      const inContact = contact && window.scrollY + window.innerHeight > contact.offsetTop + 200;
      setVisible(window.scrollY > 500 && !inContact);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="md:hidden fixed bottom-4 left-4 right-4 z-40"
          data-testid="sticky-mobile-cta"
        >
          <div className="backdrop-blur-xl bg-[#3c003a]/85 border border-[#c9006f]/30 rounded-full p-2 pl-5 flex items-center justify-between shadow-[0_12px_40px_rgba(60,0,58,0.5)]">
            <span className="font-serif italic text-[#ffc4e4] text-sm">Dates book fast —</span>
            <button
              onClick={(e) => {
                fireConfetti(e);
                scrollToSection("#contact");
              }}
              className="btn-gold rounded-full px-5 py-2.5 text-sm font-bold"
              data-testid="sticky-book-date-btn"
            >
              Book Your Date
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
